import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { AlertCircle, CheckCircle, MessageSquare, Users, Loader, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

// --- COMPONENTE DE CARITAS PERSONALIZADAS (SVG) ---
const FaceIcon = ({ rating, color, size = 24 }) => {
    const strokeWidth = 2;
    const BaseFace = ({ children }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.1" />
            {children}
        </svg>
    );

    if (rating === 1) return (<BaseFace><path d="M8 9l2 2" /><path d="M16 9l-2 2" /><circle cx="9" cy="13" r="1" fill={color} stroke="none" /><circle cx="15" cy="13" r="1" fill={color} stroke="none" /><path d="M9 17c0 0 1.5-1 3-1s3 1 3 1" /></BaseFace>);
    if (rating === 2) return (<BaseFace><circle cx="9" cy="10" r="1.5" fill={color} stroke="none" /><circle cx="15" cy="10" r="1.5" fill={color} stroke="none" /><path d="M9 16c0 0 1.5-1 3-1s3 1 3 1" /></BaseFace>);
    if (rating === 3) return (<BaseFace><circle cx="9" cy="10" r="1.5" fill={color} stroke="none" /><circle cx="15" cy="10" r="1.5" fill={color} stroke="none" /><line x1="9" y1="16" x2="15" y2="16" /></BaseFace>);
    if (rating === 4) return (<BaseFace><circle cx="9" cy="10" r="1.5" fill={color} stroke="none" /><circle cx="15" cy="10" r="1.5" fill={color} stroke="none" /><path d="M9 15c0 0 1.5 2 3 2s3-2 3-2" /></BaseFace>);
    if (rating === 5) return (<BaseFace><path d="M7 10.5C7 9 9 8 9.5 9.5C10 11 8.5 12 8.5 12C8.5 12 7 11 7 10.5Z" fill={color} stroke="none" /><path d="M14.5 9.5C15 8 17 9 17 10.5C17 11 15.5 12 15.5 12C15.5 12 14 11 14.5 9.5Z" fill={color} stroke="none" /><path d="M9 15c0 0 1.5 2 3 2s3-2 3-2" /></BaseFace>);
    return null;
};

// --- COMPONENTE DEL EJE DEL GRÁFICO ---
const CustomAxisTick = ({ x, y, payload, ratingLabels, ratingColors }) => {
    const ratingId = Object.keys(ratingLabels).find(key => ratingLabels[key] === payload.value);
    const color = ratingColors[ratingId] || '#666';
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-12} y={0} width={24} height={24}>
                <FaceIcon rating={parseInt(ratingId)} color={color} size={24} />
            </foreignObject>
            <text x={0} y={40} dy={0} textAnchor="middle" fill="#888" fontSize={11}>
                {payload.value}
            </text>
        </g>
    );
};

const DashboardEncuesta = () => {
    // ESTADOS
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all');
    const [filterRating, setFilterRating] = useState(null);

    // ESTADOS DE PAGINACIÓN
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5; // <--- CAMBIA ESTO SI QUIERES MÁS FILAS (ej: 10)

    const ratingColors = { 1: '#EF4444', 2: '#F59E0B', 3: '#9CA3AF', 4: '#10B981', 5: '#059669' };
    const ratingLabels = { 1: 'Muy Enojado', 2: 'Enojado', 3: 'Normal', 4: 'Feliz', 5: 'Muy Feliz' };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let token = null;
                const authStorage = localStorage.getItem('csi-pedidos-auth');
                if (authStorage) { try { token = JSON.parse(authStorage).token; } catch (e) { } }

                const apiUrl = `https://apicsipedidos.csiltda.cl/api/surveys/dashboard-stats?period=${period}`;
                const config = { headers: { 'Accept': 'application/json' } };
                if (token) config.headers['Authorization'] = `Bearer ${token}`;

                const response = await axios.get(apiUrl, config);
                setStats(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error:", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [period]);

    // RESETEAR PAGINA SI CAMBIAN FILTROS
    useEffect(() => {
        setCurrentPage(1);
    }, [filterRating, period]);

    const downloadExcel = () => {
        if (!stats || !stats.details) return;
        const dataToExport = filterRating ? stats.details.filter(d => d.rating === filterRating) : stats.details;
        let csvContent = "data:text/csv;charset=utf-8,";
        csvContent += "Fecha,Usuario,Email,Calificación,Comentario\n";
        dataToExport.forEach(row => {
            const rowData = [
                new Date(row.created_at).toLocaleDateString(),
                row.user_name || "Anónimo",
                row.user_email || "-",
                ratingLabels[row.rating],
                `"${(row.comment || "").replace(/"/g, '""')}"`
            ];
            csvContent += rowData.join(",") + "\n";
        });
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `reporte_encuestas_${period}.csv`);
        document.body.appendChild(link);
        link.click();
    };

    const handleBarClick = (data) => {
        const ratingKey = Object.keys(ratingLabels).find(key => ratingLabels[key] === data.name);
        if (filterRating === parseInt(ratingKey)) setFilterRating(null);
        else setFilterRating(parseInt(ratingKey));
    };

    const styles = {
        page: { padding: '40px', backgroundColor: '#f0f2f5', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif', color: '#333' },
        header: { backgroundColor: '#ffffff', padding: '24px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', flexWrap: 'wrap', gap: '20px' },
        title: { fontSize: '24px', fontWeight: 'bold', margin: 0 },
        filterContainer: { display: 'flex', gap: '10px', backgroundColor: '#f3f4f6', padding: '5px', borderRadius: '10px' },
        filterBtn: (active) => ({ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px', backgroundColor: active ? 'white' : 'transparent', color: active ? '#2563eb' : '#6b7280', boxShadow: active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }),
        grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' },
        chartSection: { backgroundColor: '#ffffff', borderRadius: '20px', padding: '32px', boxShadow: '0 10px 15px rgba(0,0,0,0.05)', marginBottom: '32px' },
        tableSection: { backgroundColor: '#ffffff', borderRadius: '20px', padding: '32px', boxShadow: '0 10px 15px rgba(0,0,0,0.05)' },
        table: { width: '100%', borderCollapse: 'collapse', marginTop: '20px' },
        th: { textAlign: 'left', padding: '15px', borderBottom: '2px solid #f0f0f0', color: '#9ca3af', fontSize: '13px', textTransform: 'uppercase' },
        td: { padding: '15px', borderBottom: '1px solid #f0f0f0', fontSize: '14px', verticalAlign: 'middle' },
        badgeContainer: (rating) => ({ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: ratingColors[rating] + '15', color: ratingColors[rating], padding: '6px 12px', borderRadius: '20px', fontWeight: 'bold', fontSize: '13px', width: 'fit-content' }),
        pagination: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '20px', fontSize: '14px', color: '#666' },
        pageBtn: (disabled) => ({ display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: disabled ? '#f9fafb' : 'white', color: disabled ? '#d1d5db' : '#374151', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s' })
    };

    if (loading) return <div style={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center' }}><Loader className="animate-spin" size={40} color="#3b82f6" /></div>;
    if (!stats) return <div>No hay datos.</div>;

    const chartData = Object.keys(ratingLabels).map(r => ({ name: ratingLabels[r], count: stats.distribution[r] || 0, color: ratingColors[r] }));

    // LÓGICA DE PAGINACIÓN
    const allTableData = filterRating ? stats.details.filter(d => d.rating === filterRating) : stats.details;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = allTableData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(allTableData.length / itemsPerPage);

    return (
        <div style={styles.page}>
            <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={styles.header}>
                <div><h1 style={styles.title}>Panel de Satisfacción</h1><p style={{ margin: '5px 0 0', color: '#666', fontSize: '14px' }}>Datos en tiempo real</p></div>
                <div style={styles.filterContainer}>
                    {['today', 'week', 'month', 'all'].map(p => (
                        <button key={p} onClick={() => setPeriod(p)} style={styles.filterBtn(period === p)}>
                            {p === 'today' ? 'Hoy' : p === 'week' ? '7 Días' : p === 'month' ? '30 Días' : 'Todo'}
                        </button>
                    ))}
                </div>
            </motion.div>

            <div style={styles.grid}>
                <KPICard title="Respuestas" value={stats.total_responses} icon={<MessageSquare size={24} color="#3b82f6" />} color="#3b82f6" />
                <KPICard title="Satisfacción" value={`${stats.satisfaction_rate}%`} icon={<CheckCircle size={24} color="#10b981" />} color="#10b981" />
                <KPICard title="Alertas" value={stats.alert_count} icon={<AlertCircle size={24} color="#ef4444" />} color="#ef4444" isAlert />
            </div>

            <div style={styles.chartSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h3 style={{ fontWeight: 'bold', fontSize: '18px' }}>Distribución de Emociones</h3>
                    {filterRating && (
                        <button onClick={() => setFilterRating(null)} style={{ background: '#f3f4f6', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>
                            Ver Todo (Filtro Activo) ✕
                        </button>
                    )}
                </div>
                <div style={{ height: '350px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} interval={0} tick={<CustomAxisTick ratingLabels={ratingLabels} ratingColors={ratingColors} />} height={60} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#888' }} />
                            <Tooltip cursor={{ fill: '#f4f4f4' }} contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 5px 15px rgba(0,0,0,0.1)' }} />
                            <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={50} onClick={handleBarClick} style={{ cursor: 'pointer' }}>
                                {chartData.map((e, i) => <Cell key={i} fill={e.color} stroke={filterRating && ratingLabels[filterRating] !== e.name ? 'transparent' : ''} opacity={filterRating && ratingLabels[filterRating] !== e.name ? 0.3 : 1} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p style={{ textAlign: 'center', fontSize: '12px', color: '#999', marginTop: '0px' }}>✨ Tip: Haz clic en una barra para filtrar la lista</p>
            </div>

            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={styles.tableSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontWeight: 'bold', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Users size={20} /> Comentarios Recientes
                    </h3>
                    <button onClick={downloadExcel} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600' }}>
                        <Download size={18} /> Exportar Excel
                    </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fecha</th>
                                <th style={styles.th}>Usuario</th>
                                <th style={styles.th}>Calificación</th>
                                <th style={styles.th}>Comentario</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? currentItems.map((row, i) => (
                                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#fcfcfc' }}>
                                    <td style={styles.td}>{new Date(row.created_at).toLocaleDateString()}</td>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: 'bold', color: '#374151' }}>{row.user_name || "Anónimo"}</div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{row.user_email}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.badgeContainer(row.rating)}>
                                            <FaceIcon rating={row.rating} color={ratingColors[row.rating]} size={20} />
                                            <span>{ratingLabels[row.rating]}</span>
                                        </div>
                                    </td>
                                    <td style={{ ...styles.td, maxWidth: '300px', color: '#4b5563' }}>{row.comment || "Sin comentario"}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#999' }}>No hay datos para este filtro</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- CONTROLES DE PAGINACIÓN --- */}
                {totalPages > 1 && (
                    <div style={styles.pagination}>
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            style={styles.pageBtn(currentPage === 1)}
                        >
                            <ChevronLeft size={16} /> Anterior
                        </button>
                        <span>Página <b>{currentPage}</b> de <b>{totalPages}</b></span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            style={styles.pageBtn(currentPage === totalPages)}
                        >
                            Siguiente <ChevronRight size={16} />
                        </button>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

const KPICard = ({ title, value, icon, color, isAlert }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '25px', boxShadow: '0 4px 10px rgba(0,0,0,0.03)', borderLeft: `4px solid ${color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><p style={{ fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</p><h2 style={{ fontSize: '28px', fontWeight: '800', margin: '5px 0 0', color: '#1f2937' }}>{value}</h2></div>
        <div style={{ padding: '12px', backgroundColor: `${color}15`, borderRadius: '12px' }}>{icon}</div>
    </div>
);

export default DashboardEncuesta;